import React, { useEffect, useState } from 'react';
import { Skeleton, Modal } from 'antd';
import { Reason } from 'types';
import { useDispatch, useSelector } from 'react-redux';

import history from 'app-router/history';
import ReasonTable from 'components/templates/reason-table';
import ButtonPrimary from 'components/molecules/button-primary';
import Layout from 'components/templates/layout';
import { useIntl } from 'common/config/i18n';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import {
  reasonsActions,
  reasonsSelectors,
  reasonActions,
  reasonSelectors,
  UserInfoSelectors,
  ClearStateSliceActions,
  TranslationLanguagesSelectors,
} from 'common/config/store';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import { isRequestError } from 'common/utils/error';
import MessageModal from 'components/organisms/message-modal';

const { StyledListWrapper, StyledListTitleWrapper, StyledListBoldWrapper, StyledListButtonWrapper } = StyledListUtil;

const Reasons: React.FC<Record<string, never>> = () => {
  const dispatch = useDispatch();
  const { t } = useIntl();
  const [modalVisible, setModalVisible] = useState(false);
  const [reason, setReason] = useState({ id: 0, name: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const status = useSelector(reasonsSelectors.statusSelector);
  const canWriteReasons = useSelector(UserInfoSelectors.hasWriteAccess('Reasons'));
  const error = useSelector(reasonSelectors.errorSelector);
  const errorType = useSelector(reasonSelectors.errorTypeSelector);
  const translationLanguages = useSelector(TranslationLanguagesSelectors.translationLanguagesSelector);
  const reasonsWithEnabledTranslations = useSelector(reasonsSelectors.reasonsWithEnabledTranslationsSelector);

  useEffect(() => {
    dispatch(reasonsActions.fetchReasons());
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'Reason' }));
  }, [dispatch]);

  useEffect(() => {
    if (errorType && errorType === 'delete') setShowDeleteModal(true);
  }, [errorType]);

  const onClickCreateHandler = (): void => {
    return history.push('/reason');
  };

  const onClickEditHandler = (reason: Reason): void => {
    return history.push(`/reason/${reason?.id}`);
  };

  const onDeleteHandler = (reason: Reason): void => {
    setReason({ id: reason.id, name: reason?.name });
    setModalVisible(true);
  };

  const onModalConfirm = (shouldRemove: boolean) => {
    if (shouldRemove) {
      dispatch(reasonActions.deleteReason(reason.id.toString()));
    }
    setModalVisible(false);
  };

  return (
    <div>
      <Modal
        title={t('Modal', 'REASON_MODAL_TITLE')}
        footer={null}
        open={modalVisible}
        onCancel={() => onModalConfirm(false)}
      >
        <ModalContentConfirm
          title={
            <div>
              {t('Modal', 'REASON_DELETE_CONFIRM')}
              <StyledListBoldWrapper>{reason.name}</StyledListBoldWrapper>
            </div>
          }
          onConfirm={onModalConfirm}
        />
      </Modal>

      <MessageModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'Reason' }));
        }}
        errorMessage={error && isRequestError(error) ? undefined : error}
      />
      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('ReasonPage', 'REASON_REPOSITORY_PAGE_H2')}</StyledMainTitle>
          <StyledListButtonWrapper>
            {canWriteReasons && (
              <ButtonPrimary onClick={onClickCreateHandler}>{t('Buttons', 'ADD_REASON')}</ButtonPrimary>
            )}
          </StyledListButtonWrapper>
        </StyledListTitleWrapper>
        {status === 'loading' && <Skeleton />}
        {status === 'success' && (
          <ReasonTable
            canWrite={canWriteReasons}
            onClickDelete={onDeleteHandler}
            onClickEdit={onClickEditHandler}
            data={reasonsWithEnabledTranslations}
            translationLanguages={translationLanguages}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};

export default Reasons;
